import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'
import { newId } from '@/utils.js'

const getDefaultState = () => {
  return {
    data: null, // Changed from array to object for document-based approach
    ref: null
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return state.data || {}
  },
  ref(state) {
    return state.ref
  },
  recipes(state) {
    // Return object of recipes where hidden == false or hidden property doesn't exist
    if (!state.data) return {}

    const result = {}
    Object.entries(state.data).forEach(([id, recipe]) => {
      if (recipe && !recipe.hidden) {
        result[id] = recipe
      }
    })
    return result
  },
  recipesNotHidden(state) {
    // Same as recipes getter - return non-hidden recipes
    if (!state.data) return {}

    const result = {}
    Object.entries(state.data).forEach(([id, recipe]) => {
      if (!recipe.hidden) {
        result[id] = recipe
      }
    })
    return result
  },
  recipesHidden(state) {
    // Return object of recipes where hidden property exists and is true
    if (!state.data) return {}

    const result = {}
    Object.entries(state.data).forEach(([id, recipe]) => {
      if (recipe.hidden) {
        result[id] = recipe
      }
    })
    return result
  },
  recipeIds(state) {
    return state.data ? Object.keys(state.data) : []
  },
  recipeIdsActive(state) {
    // Return recipe ids where hidden == false and active != false
    if (!state.data) return []

    return Object.entries(state.data)
      .filter(([, recipe]) => !recipe.hidden && recipe.active !== false)
      .map(([id]) => id)
  },
  recipeIdsNotActive(state) {
    // Return recipe ids where hidden == true or active == false
    if (!state.data) return []

    return Object.entries(state.data)
      .filter(([, recipe]) => recipe.hidden || recipe.active === false)
      .map(([id]) => id)
  },
  recipeIdsHidden(state) {
    // Return recipe ids where hidden property exists and is true
    if (!state.data) return []

    return Object.entries(state.data)
      .filter(([, recipe]) => recipe.hidden)
      .map(([id]) => id)
  },
  recipeIdsNotHidden(state) {
    // Return recipe ids where hidden == false or hidden property doesn't exist
    if (!state.data) return []

    return Object.entries(state.data)
      .filter(([, recipe]) => !recipe.hidden)
      .map(([id]) => id)
  },
  recipeById: (state) => (id) => {
    // Returns recipe regardless of active or hidden value
    return state.data && state.data[id] ? state.data[id] : null
  },
  sortRecipeIdsByRecipeName: (state, getters) => (ids) => {
    return ids.sort((a, b) =>
      getters['recipeName'](a)
        .toLowerCase()
        .localeCompare(getters['recipeName'](b).toLowerCase())
    )
  },
  recipeName: (state, getters) => (id) => {
    try {
      return getters.recipes[id].name
    } catch {
      return null
    }
  },
  recipeUnitType: (state, getters) => (id) => {
    try {
      return getters.recipes[id].unitType
    } catch {
      return null
    }
  },
  isRecipe: (state) => (id) => {
    return state.data && Object.prototype.hasOwnProperty.call(state.data, id)
  },
  isHidden: (state, getters) => (id) => {
    try {
      const recipe = getters.recipeById(id)
      if (!recipe || !Object.prototype.hasOwnProperty.call(recipe, 'hidden')) {
        return false
      }
      return recipe.hidden ? true : false
    } catch {
      return false
    }
  },
  isRecipeActive: (state, getters) => (id) => {
    try {
      const recipe = getters.recipeById(id)
      if (!recipe || !Object.prototype.hasOwnProperty.call(recipe, 'active')) {
        return true
      }
      return recipe.active ? true : false
    } catch {
      return false
    }
  },
  isTransferable: (state, getters) => (id) => {
    try {
      const recipe = getters.recipeById(id)
      if (
        !recipe ||
        !Object.prototype.hasOwnProperty.call(recipe, 'transferable')
      ) {
        return true
      }
      return recipe.transferable !== false
    } catch {
      return false
    }
  }
}

const actions = {
  bindStore: firestoreAction(({ bindFirestoreRef, commit }) => {
    // Point to the single recipes document
    commit('setRef', db.collection('config').doc('recipes'))
    return bindFirestoreRef('data', state.ref)
  }),

  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),

  create: async function ({ getters, dispatch }, payload) {
    const id = newId()
    const data = {
      ...payload,
      createdAt: Timestamp.now(),
      status: 'active',
      id: id
    }
    console.log('recipes/create', id, data)

    // Update the recipes document with the new recipe
    return dispatch('updateItem', { id, data })
      .then(() => id)
      .catch((err) => err)
  },

  delete: async function ({ dispatch }, id) {
    console.log('recipes/delete', id)
    if (!id) throw new Error('No ID provided.')
    return dispatch('updateItem', { id, data: { status: 'deleted' } })
  },

  // updateItem({ getters, rootGetters }, { id, data }) {
  //   console.log('recipes/updateItem', id, data)

  //   // Create update object with only the recipe being modified
  //   const updateData = {}
  //   updateData[id] = {
  //     ...data,
  //     updatedAt: Timestamp.now(),
  //     updatedBy: rootGetters['user/id']
  //   }

  //   // Update the recipe in the document
  //   return getters.ref.set(updateData, { merge: true })
  // },

  updateItem({ getters, rootGetters }, { id, data }) {
    console.log('recipes/updateItem', id, data)
    console.log('exiting')
    return
    // const updateData = {
    //   ...data,
    //   updatedAt: Timestamp.now(),
    //   updatedBy: rootGetters['user/id']
    // }

    // // Use field path notation for proper merging
    // const updateObject = {}
    // updateObject[id] = updateData

    // return getters.ref.update(updateObject)
  },

  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const recipes = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default recipes
